import { createContext, useContext } from 'react';

/**
 * 浮层宿主上下文：把 StructuredEditor 解析后的浮层容器传给需要 createPortal
 * 的叶子节点（SkillButton / ReportUrlNode / reportDropdownShared / StepsPreviewOverlay）。
 *
 * 为什么需要它：ConfigProvider 的 getPopupContainer 只对 antd 组件（Tooltip/Select/
 * Dropdown/DatePicker/Cascader）生效，管不到我们手写的 ReactDOM.createPortal。这些
 * createPortal 原本硬编码挂到 document.body，在 Qiankun 子应用里 body 被沙箱代理，
 * 卸载时 React 记录的父节点与沙箱实际父节点不一致 → removeChild 抛错白屏。
 *
 * 默认挂到组件自己渲染、随组件子树一起 mount/unmount 的 host；消费端传入
 * getPopupContainer 时则统一使用外部容器。两条路径均由 React 持有 Portal 父子关系，
 * 避免手动搬动 DOM 引发 removeChild。Provider 外回退 document.body 保底。
 */
export const PopupHostContext = createContext<HTMLElement | null>(null);

export const usePopupHost = (): HTMLElement | null => useContext(PopupHostContext);

/**
 * 全屏遮罩类浮层（如 StepsPreviewOverlay 教程弹层）的专属宿主，与 PopupHostContext 分离：
 * 「贴触发点定位」的浮层（技能面板/下拉）必须留在 popupHost 以保持与编辑器同源的层级表现，
 * 而全屏遮罩挂 popupHost 会被业务祖先的 transform/backdrop-filter 困成小框、被兄弟节点穿透。
 * 消费端经 getFullscreenOverlayContainer prop 指定页面级容器（如子应用 #root 首子节点）后，
 * 全屏浮层与业务自己的全屏蒙层（报告查看器等）同挂点、同层级语义。为 null 时回退 popupHost。
 */
export const FullscreenOverlayHostContext = createContext<HTMLElement | null>(null);

export const useFullscreenOverlayHost = (): HTMLElement | null =>
  useContext(FullscreenOverlayHostContext);

/**
 * 全屏大图预览（antd Image preview）专用挂载容器解析。
 *
 * 与缩略图/下拉等「贴着编辑器」的浮层不同，全屏大图预览必须铺满整个视口、压在所有内容
 * （含消费端左侧 layout 侧边栏）之上。
 *
 * 为什么直接挂 `document.body` 而不是 `#root` 或内嵌 host：
 * 1. 内嵌 `.rte-popup-host` 虽 z-index 高，但它住在 `.rich-text-editor` 子树内，而编辑器根带
 *    `backdrop-filter: blur(...)`——按 CSS 规范，backdrop-filter 非 none 的元素会成为其所有
 *    `position:fixed` 后代的 **containing block**，于是预览的 fixed 不再相对视口、而相对编辑器框
 *    定位 + 被其 `overflow:hidden` 裁剪 → 遮罩只覆盖聊天区，盖不住左侧 layout（用户实测：遮罩
 *    左边界恰好贴聊天区左缘）。z-index 再高也无效，因为层级是在被锁的 containing block 内比较。
 * 2. 之前回退到 `#root`：在 Qiankun 子应用里，layout-shell 挂载时子应用的 `#root` 可能被沙箱
 *    重命名/剥离（React 直接挂到 qiankun 容器），`closest('#root')` 返回 null → 回退到内嵌 host
 *    → 同样被 backdrop-filter 锁住。靠 `#root` 不稳。
 *
 * `document.body` 永远存在、是视口级、祖先链无 backdrop-filter/transform，fixed 预览必然铺满
 * 全屏。图片预览是用户主动关闭（点遮罩/ESC）的瞬态浮层，受控卸载时 chip 仍在、父子关系一致，
 * 不会触发「编辑器整树被 Qiankun 沙箱撕掉时 removeChild 找不到父节点」那类崩溃。host 仅作
 * body 不可用（理论上不会发生）时的最终兜底。
 */
export const resolvePreviewContainer = (host: HTMLElement | null): HTMLElement => {
  return document.body ?? host ?? document.documentElement;
};
